import usePublishedAtFilter from '@/composables/goal/published-at-filter';
import { AND, CREATED_AT, OR, RESULT, UID, UID_IN, VAR } from 'shared/api/query/constants';
import { applyBaseFilter } from '@/lib/filter/base-filter';
import { combine, notHasFilter } from 'shared/api/query/filter';
import { computed, ref } from 'vue';
import { goal as goalConfig, propertyValue as propertyValueConfig, user as userConfig } from 'shared/api/query/configs.json';
import { goalDetailChildren } from '@/api/query/nebula/goal';
import { propertyType } from 'shared/constants.json';
import { reverseEdge } from 'shared/api/query/edges';
import { unique } from 'shared/lib/array/array';

export default function useQuickActions(goalsSvc, updatesSvc, goalProperties, selectedGoalCycles, selectedUnassignedGoalCycle, loggedInUser) {
  const lastUserUpdate = ref(null);
  const userProps = goalProperties.value.filter((p) => p.type === propertyType.user);
  const userPropIds = userProps.map((p) => p.uid);
  const { publishedAtFilter } = usePublishedAtFilter();
  const init = () => {
    const p1 = fetchGoals();
    const p2 = updatesSvc.fetchUpdatesForUser(
      {
        pagination: { itemsPerPage: 1, page: 1 },
        order: [{ attr: CREATED_AT, desc: true }],
      },
    ).then((res) => {
      if (res.length === 0) {
        return;
      }
      lastUserUpdate.value = res[0];
    });

    return Promise.all([p1, p2]);
  };

  const goalsByUserProperties = ref({});
  const queryGoalsByUserProperties = (cycles, includeUnassigned) => {
    const selectedCyclesFilter = {
      func: {
        name: UID_IN,
        attr: goalConfig.edges.goalCycle,
        args: cycles.map((c) => ({ value: `${c.uid}` })),
      },
    };
    const selectedUnassignedCycleFilter = notHasFilter(goalConfig.edges.goalCycle);

    let cyclesFilter = { func: { name: UID, uid: [0] } };
    if (includeUnassigned) {
      if (cycles.length > 0) {
        cyclesFilter = combine(OR, [selectedCyclesFilter, selectedUnassignedCycleFilter]);
      } else {
        cyclesFilter = selectedUnassignedCycleFilter;
      }
    } else if (cycles.length > 0) {
      cyclesFilter = selectedCyclesFilter;
    }

    const queries = applyBaseFilter({
      queries: [
        {
          alias: VAR,
          func: { name: UID },
          uid: [loggedInUser.value.uid],
          model: userConfig.model,
          children: [
            {
              attr: reverseEdge(propertyValueConfig.edges.users),
              model: propertyValueConfig.model,
              children: [
                {
                  attr: reverseEdge(goalConfig.edges.properties),
                  model: goalConfig.model,
                  var: 'property_goals',
                },
              ],
            },
          ],
        },
        {
          alias: RESULT,
          func: {
            name: UID,
            needsVar: [{ name: 'property_goals', typ: 1 }],
          },
          needsVar: [{ name: 'property_goals', typ: 1 }],
          model: goalConfig.model,
          filter: cyclesFilter,
          children: [
            ...goalDetailChildren({ childrenFilter: null, childrenOrder: [], customFuncs: [] }),
          ],
        },
      ],
      operator: AND,
      baseFilter: publishedAtFilter,
    });
    return goalsSvc.query(queries).then((data) => {
      const result = userProps.reduce((r, n) => {
        r[n.uid] = { property: n, goals: [] };
        return r;
      }, {});

      return goalsSvc.selectMultiple(data[RESULT].map((g) => g.uid)).reduce((res, next) => {
        const propIds = next.properties
          .filter((p) => userPropIds.includes(p.property.uid)
              && p.users.map((u) => u.uid).includes(loggedInUser.value.uid))
          .map((p) => p.property.uid);

        propIds.forEach((id) => {
          res[id].goals.push(next);
        });
        return res;
      }, result);
    });
  };

  const withGoals = (grouped) => Object.values(grouped).filter((g) => g.goals.length > 0);

  const fetchGoals = () => queryGoalsByUserProperties(selectedGoalCycles.value, selectedUnassignedGoalCycle.value)
    .then((res) => {
      goalsByUserProperties.value = res;
    });

  const fetchGoalsForCycles = (cycles) => queryGoalsByUserProperties(cycles, selectedUnassignedGoalCycle.value)
    .then(withGoals);

  const goalsFromLastUpdate = computed(() => {
    if (lastUserUpdate.value === null) {
      return [];
    }
    const goalIds = lastUserUpdate.value.goalActivities
      .filter((ga) => ga.referenced === undefined)
      .map((ga) => ga.goal.uid);
    return goalsSvc.selectMultiple(unique(goalIds));
  });

  return {
    init,
    goalsFromLastUpdate,
    goalsByUserProperties: computed(() => withGoals(goalsByUserProperties.value)),
    fetchGoalsForCycles,
  };
}
